import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import { trackUxEvent } from '../../utils/telemetry';
import { RECORD_TYPE_LABELS } from './constants';
import ProviderInfoStep from './request-form/ProviderInfoStep';
import RecordSelectionStep from './request-form/RecordSelectionStep';
import DeliveryStep from './request-form/DeliveryStep';
import ReviewStep from './request-form/ReviewStep';

// Request Form Component
/** Purpose: Renders request form. Inputs: { onBack, onCreated, onUpdated, onEditComplete, initialData }. Output: React render output (JSX). */
export default function RequestForm({ onBack, onCreated, onUpdated, onEditComplete, initialData }) {
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const draftKey = 'medcaddy:provider-request-draft';
  const restoreAttempted = useRef(false);
  const normalizeDate = useCallback((value) => {
    if (!value) return '';
    try {
      return new Date(value).toISOString().split('T')[0];
    } catch (err) {
      return '';
    }
  }, []);
  const buildInitial = useCallback(() => ({
    providerName: initialData?.providerName || '',
    providerTitle: initialData?.providerTitle || '',
    organization: initialData?.organization || '',
    department: initialData?.department || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    postalCode: initialData?.postalCode || '',
    phone: initialData?.phone || '',
    fax: initialData?.fax || '',
    email: initialData?.email || '',
    providerNpi: initialData?.providerNpi || '',
    npiVerified: initialData?.npiVerified || false,
    recordTypes: initialData?.recordTypes || [],
    dateRangeStart: normalizeDate(initialData?.dateRangeStart),
    dateRangeEnd: normalizeDate(initialData?.dateRangeEnd),
    specificRecords: initialData?.specificRecords || '',
    urgency: initialData?.urgency || 'routine',
    notes: initialData?.notes || '',
    deliveryMethod: initialData?.deliveryMethod || 'EMAIL',
    sendToAnotherProvider: Boolean(
      initialData?.destinationProviderName ||
      initialData?.destinationOrganization ||
      initialData?.destinationEmail
    ),
    destinationProviderName: initialData?.destinationProviderName || '',
    destinationOrganization: initialData?.destinationOrganization || '',
    destinationDepartment: initialData?.destinationDepartment || '',
    destinationAddress: initialData?.destinationAddress || '',
    destinationCity: initialData?.destinationCity || '',
    destinationState: initialData?.destinationState || '',
    destinationPostalCode: initialData?.destinationPostalCode || '',
    destinationPhone: initialData?.destinationPhone || '',
    destinationFax: initialData?.destinationFax || '',
    destinationEmail: initialData?.destinationEmail || '',
    sendCopyToUser: Boolean(initialData?.sendCopyToUser)
  }), [initialData, normalizeDate]);
  const [formData, setFormData] = useState(buildInitial);
  const [lastAutoSaved, setLastAutoSaved] = useState('');
  const [draftRestored, setDraftRestored] = useState(false);
  const submittedRef = useRef(false);
  const stepRef = useRef(step);
  const [savedProviders, setSavedProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [saveProvider, setSaveProvider] = useState(false);
  const [selectedExistingProvider, setSelectedExistingProvider] = useState(false);
  const getDeliveryErrors = useCallback((data) => {
    const errors = {};
    if (data.deliveryMethod === 'EMAIL' && !data.email.trim()) {
      errors.email = 'Email is required for email delivery.';
    }
    if (data.deliveryMethod === 'FAX' && !data.fax.trim()) {
      errors.fax = 'Fax is required for fax delivery.';
    }
    if (data.deliveryMethod === 'MAIL') {
      if (!data.address.trim()) errors.address = 'Street address is required for mail delivery.';
      if (!data.city.trim()) errors.city = 'City is required for mail delivery.';
      if (!data.state.trim()) errors.state = 'State is required for mail delivery.';
      if (!data.postalCode.trim()) errors.postalCode = 'ZIP is required for mail delivery.';
    }
    if (data.deliveryMethod === 'PORTAL' && !data.notes.trim() && !data.specificRecords.trim()) {
      errors.portal = 'Add portal instructions or record details for portal delivery.';
    }
    return errors;
  }, []);
  const deliveryErrors = useMemo(
    () => getDeliveryErrors(formData),
    [formData, getDeliveryErrors]
  );
  /** Purpose: Executes input class. Inputs: hasError. Output: Operation result. */
  const inputClass = (hasError) => (
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
      hasError ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-purple-500'
    }`
  );

  useEffect(() => {
    setFormData(buildInitial());
    if (!initialData?.id) {
      setLastAutoSaved('');
      setDraftRestored(false);
      restoreAttempted.current = false;
    }
  }, [buildInitial, initialData?.id]);

  useEffect(() => {
    if (initialData?.id || restoreAttempted.current) return;
    restoreAttempted.current = true;
    try {
      const stored = localStorage.getItem(draftKey);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (!parsed?.data) return;
      setFormData(prev => ({ ...prev, ...parsed.data }));
      setLastAutoSaved(parsed.updatedAt || '');
      setDraftRestored(true);
    } catch (err) {
      console.warn('Failed to restore draft', err);
    }
  }, [initialData?.id]);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    if (initialData?.id) return;
    trackUxEvent('provider_request_step_view', { step });
  }, [initialData?.id, step]);

  useEffect(() => {
    return () => {
      if (!initialData?.id && !submittedRef.current && stepRef.current > 1) {
        trackUxEvent('provider_request_step_dropoff', { step: stepRef.current });
      }
    };
  }, [initialData?.id]);

  /** Purpose: Executes is empty draft. Inputs: data. Output: Operation result. */
  const isEmptyDraft = (data) => (
    !data.providerName.trim()
    && !data.organization.trim()
    && !data.department.trim()
    && !data.address.trim()
    && !data.city.trim()
    && !data.state.trim()
    && !data.postalCode.trim()
    && !data.phone.trim()
    && !data.fax.trim()
    && !data.email.trim()
    && !data.providerNpi.trim()
    && data.recordTypes.length === 0
    && !data.dateRangeStart
    && !data.dateRangeEnd
    && !data.specificRecords.trim()
    && !data.notes.trim()
  );

  useEffect(() => {
    if (initialData?.id) return;
    const timeout = setTimeout(() => {
      try {
        if (isEmptyDraft(formData)) {
          localStorage.removeItem(draftKey);
          setLastAutoSaved('');
          return;
        }
        const payload = {
          data: formData,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
        setLastAutoSaved(payload.updatedAt);
      } catch (err) {
        console.warn('Failed to autosave draft', err);
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [formData, initialData?.id]);

  useEffect(() => {
    let cancelled = false;
    setProvidersLoading(true);
    apiFetch('/providers')
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (cancelled) return;
        const normalizedProviders = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.providers)
              ? data.providers
              : [];
        setSavedProviders(normalizedProviders);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setProvidersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSelectProvider = (providerId) => {
    if (!providerId) return;
    const provider = savedProviders.find(p => p.id === providerId);
    if (!provider) return;
    setSelectedExistingProvider(true);
    setSaveProvider(false);
    setFormData(prev => ({
      ...prev,
      providerName: provider.name || '',
      providerTitle: provider.title || '',
      organization: provider.organization || '',
      address: provider.addressLine1 || '',
      city: provider.city || '',
      state: provider.state || '',
      postalCode: provider.postalCode || '',
      phone: provider.phone || '',
      fax: provider.fax || '',
      email: provider.email || '',
      providerNpi: provider.npi || '',
      npiVerified: false
    }));
  };

  /** Purpose: Handles npi select. Inputs: provider. Output: Operation result. */
  const handleNpiSelect = (provider) => {
    setFormData({
      ...formData,
      providerName: provider.providerName || formData.providerName,
      providerTitle: provider.credential || '',
      organization: provider.organization || formData.organization,
      address: provider.address || '',
      phone: provider.phone || '',
      fax: provider.fax || '',
      providerNpi: provider.npi,
      npiVerified: provider.verified
    });
  };

  /** Purpose: Handles record type toggle. Inputs: type. Output: Operation result. */
  const handleRecordTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      recordTypes: prev.recordTypes.includes(type)
        ? prev.recordTypes.filter(t => t !== type)
        : [...prev.recordTypes, type]
    }));
  };

  /** Purpose: Handles submit. Inputs: none. Output: Promise with operation result. */
  const handleSubmit = async () => {
    setFormError('');
    const isEditing = Boolean(initialData?.id);
    if (!formData.providerName || !formData.organization || formData.recordTypes.length === 0) {
      setFormError('Please fill in required fields and select at least one record type.');
      return;
    }

    if (formData.sendToAnotherProvider) {
      if (!formData.destinationProviderName || !formData.destinationOrganization) {
        setFormError('Destination provider name and organization are required.');
        return;
      }
      if (!formData.email.trim()) {
        setFormError('Sending provider email is required for provider-to-provider transfer.');
        return;
      }
    }

    if (Object.keys(deliveryErrors).length > 0) {
      const message = Object.values(deliveryErrors)[0];
      setFormError(message);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiFetch(isEditing ? `/provider-requests/${initialData.id}` : '/provider-requests', {
        method: isEditing ? 'PATCH' : 'POST',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let message = isEditing ? 'Failed to update request' : 'Failed to create request';
        try {
          const data = await response.json();
          message = data.error || message;
        } catch (err) {
          // no-op
        }
        throw new Error(message);
      }

      const request = await response.json();

      if (saveProvider && !selectedExistingProvider && formData.providerName && formData.organization) {
        try {
          await apiFetch('/providers', {
            method: 'POST',
            body: JSON.stringify({
              name: formData.providerName,
              title: formData.providerTitle || undefined,
              organization: formData.organization,
              email: formData.email || undefined,
              phone: formData.phone || undefined,
              fax: formData.fax || undefined,
              addressLine1: formData.address || undefined,
              city: formData.city || undefined,
              state: formData.state || undefined,
              postalCode: formData.postalCode || undefined,
              npi: formData.providerNpi || undefined
            })
          });
        } catch (err) {
          console.warn('Failed to save provider', err);
        }
      }

      if (isEditing) {
        if (onUpdated) onUpdated(request);
        if (onEditComplete) onEditComplete(request);
      } else {
        submittedRef.current = true;
        trackUxEvent('provider_request_completion', {
          deliveryMethod: formData.deliveryMethod,
          recordTypeCount: formData.recordTypes.length,
          forwardedToProvider: Boolean(formData.sendToAnotherProvider)
        });
        onCreated(request);
        try {
          localStorage.removeItem(draftKey);
        } catch (err) {
          console.warn('Failed to clear draft', err);
        }
        setLastAutoSaved('');
        setDraftRestored(false);
      }
    } catch (err) {
      console.error('Create error:', err);
      setFormError(err.message || (isEditing ? 'Failed to update request.' : 'Failed to create request.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Purpose: Handles next step. Inputs: none. Output: Operation result. */
  const handleNextStep = () => {
    setFormError('');

    if (step === 1) {
      if (!formData.providerName || !formData.organization) {
        setFormError('Provider name and organization are required.');
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      if (formData.recordTypes.length === 0) {
        setFormError('Select at least one record type.');
        return;
      }
      setStep(3);
      return;
    }

    if (step === 3) {
      if (formData.sendToAnotherProvider) {
        if (!formData.destinationProviderName || !formData.destinationOrganization) {
          setFormError('Destination provider name and organization are required.');
          return;
        }
        if (!formData.email.trim()) {
          setFormError('Sending provider email is required for provider-to-provider transfer.');
          return;
        }
      }
      if (Object.keys(deliveryErrors).length > 0) {
        const message = Object.values(deliveryErrors)[0];
        setFormError(message);
        return;
      }
      setStep(4);
    }
  };

  const stepLabels = ['Provider', 'Records', 'Authorization', 'Review'];
  const selectedRecordLabels = formData.recordTypes.map((type) => RECORD_TYPE_LABELS[type] || type);

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to requests
      </button>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">
            {initialData?.id ? 'Edit Records Request' : 'New Records Request'}
          </h3>
          <p className="text-purple-100 text-sm">Step {step} of {totalSteps}</p>
        </div>

        <div className="p-6">
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {formError}
            </div>
          )}
          {!initialData?.id && (draftRestored || lastAutoSaved) && (
            <div className="mb-4 flex items-center justify-between text-xs text-slate-500">
              <span>
                {draftRestored ? 'Draft restored from your last session.' : 'Draft autosave enabled.'}
              </span>
              {lastAutoSaved && (
                <span>Last saved {new Date(lastAutoSaved).toLocaleTimeString()}</span>
              )}
            </div>
          )}
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {stepLabels.map((label, index) => {
              const value = index + 1;
              const active = step === value;
              const complete = step > value;
              return (
                <div key={label} className="inline-flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full border text-xs font-semibold flex items-center justify-center ${
                    complete ? 'bg-green-600 border-green-600 text-white' : active ? 'bg-purple-600 border-purple-600 text-white' : 'border-slate-300 text-slate-500'
                  }`}>
                    {value}
                  </div>
                  <span className={`text-xs font-medium ${active ? 'text-purple-700' : 'text-slate-500'}`}>{label}</span>
                </div>
              );
            })}
          </div>

          {step === 1 && (
            <ProviderInfoStep
              formData={formData}
              setFormData={setFormData}
              deliveryErrors={deliveryErrors}
              inputClass={inputClass}
              savedProviders={savedProviders}
              providersLoading={providersLoading}
              handleSelectProvider={handleSelectProvider}
              handleNpiSelect={handleNpiSelect}
              selectedExistingProvider={selectedExistingProvider}
              saveProvider={saveProvider}
              setSaveProvider={setSaveProvider}
              initialData={initialData}
            />
          )}

          {step === 2 && (
            <RecordSelectionStep
              formData={formData}
              setFormData={setFormData}
              handleRecordTypeToggle={handleRecordTypeToggle}
              deliveryErrors={deliveryErrors}
            />
          )}

          {step === 3 && (
            <DeliveryStep
              formData={formData}
              setFormData={setFormData}
            />
          )}

          {step === 4 && (
            <ReviewStep
              formData={formData}
              selectedRecordLabels={selectedRecordLabels}
            />
          )}

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => setStep((prev) => Math.max(1, prev - 1))}
              disabled={step === 1}
              className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            {step < totalSteps ? (
              <button
                onClick={handleNextStep}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || formData.recordTypes.length === 0}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {initialData?.id ? 'Update Request' : 'Create Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
